import React from 'react';
import { ownedProjects, collaborationProjects } from '../data/portfolio';
import Icon from './Icon';

export function ProjectIllustration({ project, large = false }) {
  const renderTimes = project.id === 'renderer' ? project.headlineMetric.value.match(/\d+/g).map(Number) : [];
  return (
    <div className={`project-illustration illustration-${project.id} ${large ? 'illustration-large' : ''}`} aria-hidden="true">
      <span className="illustration-label">FIG. {project.number} / {project.id === 'qa-agents' ? 'PROPOSED FLOW' : project.id === 'renderer' ? 'PRODUCTION RENDERER' : 'CONCEPTUAL VIEW'}</span>
      {project.id === 'pim' ? (
        <div className="feed-sketch">
          <div className="feed-stack"><i /><i /><i /><i /><span>{project.headlineMetric.value} products</span></div>
          <div className="feed-connector"><span>STREAM</span><Icon /></div>
          <div className="memory-box"><span>{project.metrics[1].value}</span><small>RAM envelope</small></div>
        </div>
      ) : project.id === 'renderer' ? (
        <div className="render-sketch"><div className="render-sketch-head"><span>Same workload.<br />A shorter wait.</span><strong>{project.metrics[0].value}<small>less render time</small></strong></div><div className="render-bars"><div><span>{renderTimes[0]}s</span><i /></div><div><span>{renderTimes[1]}s</span><i style={{ width: `${renderTimes[1] / renderTimes[0] * 100}%` }} /></div></div><span className="sketch-footnote">Matched staging run · 6-second video · before / after</span></div>
      ) : project.id === 'qa-agents' ? (
        <div className={`qa-sketch ${large ? 'qa-sketch-named' : ''}`}><span className="orchestrator">Orchestrator</span><div className="agent-nodes">{project.proposal.agents.map((agent, index) => <span key={agent.name}>{large ? agent.name : `0${index + 1}`}</span>)}</div><span className="human-gate">{large ? 'Test approval · Manual UAT · Release approval · Sanity response' : 'Human quality gates'}</span></div>
      ) : (
        <div className="learning-sketch"><span>Live classes</span><span>Test series</span><span>Video lectures</span><span>Analytics</span><div className="learning-core">PostgreSQL + DRF</div></div>
      )}
    </div>
  );
}

function ProjectList({ items, collaboration = false }) {
  return (
    <div className="work-list">
      {items.map((project, index) => (
        <article className={`work-row ${!collaboration && index === 0 ? 'work-featured' : ''}`} key={project.id} data-project-id={project.id}>
          <span className="work-number" aria-label={`${collaboration ? 'Collaboration' : 'Owned project'} ${Number(project.number)}`}>{project.number}</span>
          {!collaboration && <ProjectIllustration project={project} />}
          <div className="work-copy">
            <div className="work-kicker"><span>{project.company}</span><span className="status">{project.status}</span></div>
            <span className="work-subtitle">{project.subtitle}</span>
            <h3><a href={`/work/${project.id}/`}>{project.title}<Icon kind="diagonal" /></a></h3>
            <p>{project.summary}</p>
            {project.collaboration && (
              <div className="work-collaboration">
                <p>{project.collaboration.creator && <>Built by <strong>{project.collaboration.creator}</strong> · </>}My role: {project.collaboration.role || 'Collaborator'}</p>
                {project.collaboration.article?.url && <a className="text-link" href={project.collaboration.article.url} target="_blank" rel="noopener noreferrer" aria-label={`${project.collaboration.creator ? `Read ${project.collaboration.creator}’s article` : 'Read the project article'} (opens in a new tab)`}>{project.collaboration.creator ? `Read ${project.collaboration.creator}’s article` : 'Read the project article'} <Icon kind="diagonal" /></a>}
              </div>
            )}
            <div className="work-bottom"><span className="work-category">{project.category}</span><a className="text-link" href={`/work/${project.id}/`} aria-label={`Read ${project.title} case study`}>{collaboration ? 'Read the collaboration' : 'Read the study'} <Icon /></a></div>
          </div>
        </article>
      ))}
    </div>
  );
}

export default function Work({ full = false }) {
  return (
    <div id="work" className={`work-section section ${full ? 'work-index' : ''}`}>
      <section className="work-owned" aria-labelledby="work-title">
        <div className="section-heading"><div><span className="eyebrow">01 / Projects I own</span><h2 id="work-title" tabIndex={-1}>Owned work.<br /><em>From the inside out.</em></h2></div><p>The systems I built, the decisions I made, and the details that made a difference. Explore the delivered work and my QA architecture proposal.</p></div>
        <ProjectList items={ownedProjects} />
      </section>
      {collaborationProjects.length > 0 && (
        <section className="work-collaborations" aria-labelledby="collaborations-title">
          <div className="section-heading"><div><span className="eyebrow">Alongside my owned work</span><h2 id="collaborations-title" tabIndex={-1}>Collaborations.</h2></div><p>Work I contributed to, with credit to the people who led and built it.</p></div>
          <ProjectList items={collaborationProjects} collaboration />
        </section>
      )}
      {!full && <div className="work-endnote"><span>{ownedProjects.length} owned studies · {collaborationProjects.length} {collaborationProjects.length === 1 ? 'collaboration' : 'collaborations'}</span><a className="text-link" href="/work/">Explore all the work <Icon /></a></div>}
    </div>
  );
}
